/**
 * TemplateEditorDialog Component
 * Allows designers and contractors to create or edit custom BOQ starter templates.
 */

import { Layers, Plus, Trash2, X } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import type {
	CustomBOQTemplate,
	CustomTemplateItem,
	CustomTemplateSection,
	StarterBoqTier,
	UoM,
} from "@/features/projects/services/boq/boq-api.service";
import { saveCustomTemplate } from "@/features/projects/services/boq/boq-api.service";
import { StudioButton } from "@/features/studio/theme";
import { formatPricePaisa } from "@/lib/format";

interface TemplateEditorDialogProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	template?: CustomBOQTemplate | null;
	onSaved: (template: CustomBOQTemplate) => void;
}

const UOM_OPTIONS: { value: UoM; label: string }[] = [
	{ value: "sq_ft", label: "sq ft" },
	{ value: "running_ft", label: "rft" },
	{ value: "piece", label: "piece" },
	{ value: "set", label: "set" },
	{ value: "meter", label: "meter" },
	{ value: "liter", label: "liter" },
	{ value: "kg", label: "kg" },
];

export function TemplateEditorDialog({
	open,
	onOpenChange,
	template,
	onSaved,
}: TemplateEditorDialogProps) {
	const [name, setName] = useState("");
	const [description, setDescription] = useState("");
	const [projectScope, setProjectScope] = useState<"full_home" | "partial">(
		"full_home",
	);
	const [defaultTier, setDefaultTier] = useState<StarterBoqTier>("standard");
	const [sections, setSections] = useState<CustomTemplateSection[]>([]);
	const [isSaving, setIsSaving] = useState(false);

	useEffect(() => {
		if (template) {
			setName(template.name);
			setDescription(template.description || "");
			setProjectScope(template.projectScope || "full_home");
			setDefaultTier(template.defaultTier);
			setSections(
				template.sections.map((sec) => ({
					...sec,
					items: sec.items.map((it) => ({ ...it })),
				})),
			);
		} else {
			setName("");
			setDescription("");
			setProjectScope("full_home");
			setDefaultTier("standard");
			setSections([
				{
					id: `sec-${Date.now()}-1`,
					name: "General & Living Room",
					sortOrder: 1,
					items: [
						{
							id: `item-${Date.now()}-1`,
							description: "Wall painting in Premium Emulsion",
							uom: "sq_ft",
							quantity: 500,
							unitPrice: 3500, // 35 INR
							lineTotal: 1750000,
						},
					],
				},
			]);
		}
	}, [template]);

	// Section management
	const handleAddSection = () => {
		const newSection: CustomTemplateSection = {
			id: `sec-${Date.now()}`,
			name: `Section ${sections.length + 1}`,
			sortOrder: sections.length + 1,
			items: [
				{
					id: `item-${Date.now()}`,
					description: "New Item",
					uom: "sq_ft",
					quantity: 1,
					unitPrice: 10000, // 100 INR
					lineTotal: 10000,
				},
			],
		};
		setSections([...sections, newSection]);
	};

	const handleRemoveSection = (sectionId: string) => {
		if (sections.length <= 1) {
			toast.error("Template must have at least one section");
			return;
		}
		setSections(sections.filter((s) => s.id !== sectionId));
	};

	const handleUpdateSectionName = (sectionId: string, newName: string) => {
		setSections(
			sections.map((s) => (s.id === sectionId ? { ...s, name: newName } : s)),
		);
	};

	// Item management
	const handleAddItem = (sectionId: string) => {
		const newItem: CustomTemplateItem = {
			id: `item-${Date.now()}-${Math.random().toString(36).substring(2, 6)}`,
			description: "New Item Description",
			uom: "piece",
			quantity: 1,
			unitPrice: 50000, // 500 INR
			lineTotal: 50000,
		};

		setSections(
			sections.map((s) => {
				if (s.id !== sectionId) return s;
				return {
					...s,
					items: [...s.items, newItem],
				};
			}),
		);
	};

	const handleRemoveItem = (sectionId: string, itemId: string) => {
		setSections(
			sections.map((s) => {
				if (s.id !== sectionId) return s;
				if (s.items.length <= 1) {
					toast.error("A section must have at least one line item");
					return s;
				}
				return {
					...s,
					items: s.items.filter((it) => it.id !== itemId),
				};
			}),
		);
	};

	const handleUpdateItem = (
		sectionId: string,
		itemId: string,
		updates: Partial<CustomTemplateItem>,
	) => {
		setSections(
			sections.map((s) => {
				if (s.id !== sectionId) return s;
				return {
					...s,
					items: s.items.map((it) => {
						if (it.id !== itemId) return it;
						const updated = { ...it, ...updates };
						updated.lineTotal = Math.round(
							updated.quantity * updated.unitPrice,
						);
						return updated;
					}),
				};
			}),
		);
	};

	// Calculation
	const grandTotalPaisa = sections.reduce((sum, sec) => {
		return (
			sum +
			sec.items.reduce((secSum, it) => {
				return secSum + it.lineTotal;
			}, 0)
		);
	}, 0);

	const totalItemsCount = sections.reduce(
		(count, sec) => count + sec.items.length,
		0,
	);

	const handleSave = async () => {
		if (!name.trim()) {
			toast.error("Please enter a template name");
			return;
		}

		try {
			setIsSaving(true);
			const saved = await saveCustomTemplate({
				id: template?.id,
				name: name.trim(),
				slug:
					template?.slug ||
					name
						.toLowerCase()
						.replace(/[^a-z0-9]+/g, "-")
						.replace(/^-|-$/g, ""),
				description: description.trim(),
				projectScope,
				defaultTier,
				supportedProviderRoles: ["designer", "contractor"],
				isActive: true,
				isSystem: false,
				sections,
			});

			toast.success(
				template ? "Template updated successfully" : "Template created!",
			);
			onSaved(saved);
			onOpenChange(false);
		} catch (err) {
			toast.error(
				err instanceof Error ? err.message : "Failed to save template",
			);
		} finally {
			setIsSaving(false);
		}
	};

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent
				className="max-h-[90vh] max-w-4xl overflow-y-auto"
				style={{
					background: "var(--color-dashboard-paper)",
					border: "1px solid var(--color-dashboard-border)",
				}}
			>
				<DialogHeader>
					<DialogTitle
						style={{
							fontFamily: "var(--font-display)",
							fontSize: 20,
							fontWeight: 600,
							color: "var(--color-dashboard-text)",
						}}
					>
						{template ? "Edit Custom BOQ Template" : "New Custom BOQ Template"}
					</DialogTitle>
					<DialogDescription
						className="text-xs"
						style={{ color: "var(--color-dashboard-muted)" }}
					>
						Define sections, default specifications, units, and baseline rates
						for fast estimation.
					</DialogDescription>
				</DialogHeader>

				<div className="space-y-6 py-3">
					{/* Meta inputs */}
					<div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
						<div className="sm:col-span-2">
							<label
								htmlFor="tmpl-name"
								className="block text-xs font-medium mb-1.5"
								style={{ color: "var(--color-dashboard-text)" }}
							>
								Template Name *
							</label>
							<input
								id="tmpl-name"
								type="text"
								value={name}
								onChange={(e) => setName(e.target.value)}
								placeholder="e.g. Luxury 3BHK Master Specifications"
								className="w-full rounded-md px-3 py-2 text-sm outline-none"
								style={{
									border: "1px solid var(--color-dashboard-border)",
									background: "var(--color-dashboard-surface)",
									color: "var(--color-dashboard-text)",
								}}
							/>
						</div>

						<div>
							<label
								htmlFor="tmpl-scope"
								className="block text-xs font-medium mb-1.5"
								style={{ color: "var(--color-dashboard-text)" }}
							>
								Project Scope
							</label>
							<select
								id="tmpl-scope"
								value={projectScope}
								onChange={(e) =>
									setProjectScope(e.target.value as "full_home" | "partial")
								}
								className="w-full rounded-md px-3 py-2 text-sm outline-none"
								style={{
									border: "1px solid var(--color-dashboard-border)",
									background: "var(--color-dashboard-surface)",
									color: "var(--color-dashboard-text)",
								}}
							>
								<option value="full_home">Full Home</option>
								<option value="partial">Partial / Room Specific</option>
							</select>
						</div>

						<div>
							<label
								htmlFor="tmpl-tier"
								className="block text-xs font-medium mb-1.5"
								style={{ color: "var(--color-dashboard-text)" }}
							>
								Quality Tier
							</label>
							<select
								id="tmpl-tier"
								value={defaultTier}
								onChange={(e) =>
									setDefaultTier(e.target.value as StarterBoqTier)
								}
								className="w-full rounded-md px-3 py-2 text-sm outline-none"
								style={{
									border: "1px solid var(--color-dashboard-border)",
									background: "var(--color-dashboard-surface)",
									color: "var(--color-dashboard-text)",
								}}
							>
								<option value="standard">Standard</option>
								<option value="premium">Premium</option>
								<option value="luxury">Luxury</option>
							</select>
						</div>

						<div className="sm:col-span-2">
							<label
								htmlFor="tmpl-desc"
								className="block text-xs font-medium mb-1.5"
								style={{ color: "var(--color-dashboard-text)" }}
							>
								Description
							</label>
							<textarea
								id="tmpl-desc"
								value={description}
								onChange={(e) => setDescription(e.target.value)}
								placeholder="Add details about recommended room sizes, materials, or package inclusions..."
								rows={2}
								className="w-full rounded-md px-3 py-2 text-sm outline-none resize-vertical"
								style={{
									border: "1px solid var(--color-dashboard-border)",
									background: "var(--color-dashboard-surface)",
									color: "var(--color-dashboard-text)",
								}}
							/>
						</div>
					</div>

					{/* Sections & Items Builder */}
					<div className="space-y-4">
						<div className="flex items-center justify-between border-b pb-2">
							<div className="flex items-center gap-2">
								<Layers className="h-4 w-4 text-[var(--color-dashboard-accent-dark)]" />
								<h3
									className="text-sm font-semibold"
									style={{ color: "var(--color-dashboard-text)" }}
								>
									Sections & Line Items
								</h3>
							</div>
							<StudioButton
								variant="secondary"
								size="sm"
								icon={Plus}
								onClick={handleAddSection}
							>
								Add Section
							</StudioButton>
						</div>

						{sections.map((section, sIdx) => (
							<div
								key={section.id}
								className="rounded-lg p-4 space-y-3"
								style={{
									background: "var(--color-dashboard-surface)",
									border: "1px solid var(--color-dashboard-border)",
								}}
							>
								{/* Section Header */}
								<div className="flex items-center justify-between gap-3">
									<div className="flex items-center gap-2 flex-1">
										<span
											className="text-xs font-mono font-semibold"
											style={{ color: "var(--color-dashboard-muted)" }}
										>
											§{sIdx + 1}
										</span>
										<input
											type="text"
											value={section.name}
											onChange={(e) =>
												handleUpdateSectionName(section.id, e.target.value)
											}
											aria-label="Section title"
											className="rounded px-2.5 py-1 text-sm font-semibold outline-none flex-1 max-w-sm"
											style={{
												border: "1px solid var(--color-dashboard-border-soft)",
												background: "var(--color-dashboard-paper)",
												color: "var(--color-dashboard-text)",
											}}
										/>
									</div>
									<div className="flex items-center gap-2">
										<button
											type="button"
											onClick={() => handleRemoveSection(section.id)}
											className="p-1 rounded transition-colors text-dashboard-danger hover:bg-dashboard-surface"
											title="Delete section"
										>
											<Trash2 className="h-4 w-4" />
										</button>
									</div>
								</div>

								{/* Items table */}
								<div className="overflow-x-auto rounded border border-[var(--color-dashboard-border-soft)]">
									<table className="w-full text-left text-xs">
										<thead>
											<tr
												style={{
													background: "var(--color-dashboard-paper)",
													color: "var(--color-dashboard-muted)",
												}}
											>
												<th className="p-2">Item Description / Spec</th>
												<th className="p-2 w-24">Unit</th>
												<th className="p-2 w-20 text-right">Qty</th>
												<th className="p-2 w-28 text-right">Rate (₹)</th>
												<th className="p-2 w-28 text-right">Amount (₹)</th>
												<th className="p-2 w-10 text-center"></th>
											</tr>
										</thead>
										<tbody>
											{section.items.map((item) => (
												<tr
													key={item.id}
													className="border-t border-[var(--color-dashboard-border-soft)]"
													style={{
														background: "var(--color-dashboard-surface)",
													}}
												>
													<td className="p-2">
														<input
															type="text"
															value={item.description}
															onChange={(e) =>
																handleUpdateItem(section.id, item.id, {
																	description: e.target.value,
																})
															}
															placeholder="Line item description"
															className="w-full rounded px-2 py-1 text-xs outline-none"
															style={{
																border:
																	"1px solid var(--color-dashboard-border-soft)",
																background: "var(--color-dashboard-paper)",
																color: "var(--color-dashboard-text)",
															}}
														/>
													</td>
													<td className="p-2">
														<select
															value={item.uom}
															onChange={(e) =>
																handleUpdateItem(section.id, item.id, {
																	uom: e.target.value as UoM,
																})
															}
															className="w-full rounded px-2 py-1 text-xs outline-none"
															style={{
																border:
																	"1px solid var(--color-dashboard-border-soft)",
																background: "var(--color-dashboard-paper)",
																color: "var(--color-dashboard-text)",
															}}
														>
															{UOM_OPTIONS.map((opt) => (
																<option key={opt.value} value={opt.value}>
																	{opt.label}
																</option>
															))}
														</select>
													</td>
													<td className="p-2 text-right">
														<input
															type="number"
															min="0"
															step="any"
															value={item.quantity}
															onChange={(e) =>
																handleUpdateItem(section.id, item.id, {
																	quantity: Number(e.target.value) || 0,
																})
															}
															className="w-full rounded px-2 py-1 text-xs text-right outline-none"
															style={{
																border:
																	"1px solid var(--color-dashboard-border-soft)",
																background: "var(--color-dashboard-paper)",
																color: "var(--color-dashboard-text)",
															}}
														/>
													</td>
													<td className="p-2 text-right">
														<input
															type="number"
															min="0"
															step="any"
															value={item.unitPrice / 100}
															onChange={(e) =>
																handleUpdateItem(section.id, item.id, {
																	unitPrice: Math.round(
																		(Number(e.target.value) || 0) * 100,
																	),
																})
															}
															className="w-full rounded px-2 py-1 text-xs text-right outline-none"
															style={{
																border:
																	"1px solid var(--color-dashboard-border-soft)",
																background: "var(--color-dashboard-paper)",
																color: "var(--color-dashboard-text)",
															}}
														/>
													</td>
													<td className="p-2 text-right font-semibold">
														{formatPricePaisa(item.lineTotal)}
													</td>
													<td className="p-2 text-center">
														<button
															type="button"
															onClick={() =>
																handleRemoveItem(section.id, item.id)
															}
															className="transition-colors text-dashboard-muted hover:text-dashboard-danger"
															title="Remove item"
														>
															<X className="h-3.5 w-3.5" />
														</button>
													</td>
												</tr>
											))}
										</tbody>
									</table>
								</div>

								<div className="flex justify-start pt-1">
									<StudioButton
										variant="ghost"
										size="sm"
										icon={Plus}
										onClick={() => handleAddItem(section.id)}
									>
										Add Line Item
									</StudioButton>
								</div>
							</div>
						))}
					</div>
				</div>

				<DialogFooter
					className="flex flex-wrap items-center justify-between gap-4 border-t pt-4"
					style={{ borderColor: "var(--color-dashboard-border)" }}
				>
					<div className="flex items-center gap-4 text-xs">
						<span style={{ color: "var(--color-dashboard-muted)" }}>
							{sections.length} Sections · {totalItemsCount} Items
						</span>
						<span
							className="font-semibold text-sm"
							style={{ color: "var(--color-dashboard-text)" }}
						>
							Estimated Baseline: {formatPricePaisa(grandTotalPaisa)}
						</span>
					</div>

					<div className="flex items-center gap-2">
						<StudioButton
							variant="secondary"
							size="sm"
							onClick={() => onOpenChange(false)}
							disabled={isSaving}
						>
							Cancel
						</StudioButton>
						<StudioButton
							variant="primary"
							size="sm"
							onClick={handleSave}
							disabled={isSaving || !name.trim()}
						>
							{isSaving ? "Saving..." : "Save Template"}
						</StudioButton>
					</div>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
